import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarApi, HttpError } from "@/api";
import { qk } from "./queryKeys";
import type { CalEvent } from "@/types";

// RSVP only accepts a positive answer per the backend contract; "pending" is a
// server-assigned default, never something the user picks.
type RsvpChoice = "accepted" | "declined" | "tentative";

export function useCalendars() {
  return useQuery({ queryKey: qk.calendars(), queryFn: () => CalendarApi.listCalendars() });
}

export function useEvents(range: { start_ts: number; end_ts: number }, calendars?: string[]) {
  return useQuery({ queryKey: qk.events(range, calendars), queryFn: () => CalendarApi.listEvents(range, calendars) });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ev: Parameters<typeof CalendarApi.create>[0]) => CalendarApi.create(ev),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.calendar }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CalEvent> }) => CalendarApi.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.calendar }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => CalendarApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.calendar }),
  });
}

export function useNlEventParse() {
  // Not cached; debounced in the UI layer for live preview.
  return useMutation({ mutationFn: (text: string) => CalendarApi.parseNl(text) });
}

export function useEventAttendees(eventId: string | null) {
  return useQuery({
    queryKey: qk.eventAttendees(eventId || ""),
    queryFn: () => CalendarApi.listAttendees(eventId!),
    enabled: !!eventId,
    // A 404 is a legitimate "you can't see this list" outcome (caller is not
    // owner or attendee) - don't retry it. Other failures keep the default
    // single retry.
    retry: (count, err) => (err instanceof HttpError && err.status === 404 ? false : count < 1),
  });
}

export function useRsvp(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: RsvpChoice) => CalendarApi.rsvp(eventId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.eventAttendees(eventId) });
      qc.invalidateQueries({ queryKey: qk.calendar });
    },
  });
}
